/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/GoogleSignInButton.tsx
 * Release: v2.0.0 - Bottone Google Sign-In (GIS) con integrazione backend V2
 * Note: Usa Google Identity Services per ottenere id_token e autenticare su /api/auth/google
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { useToast } from '../ui/ToastProvider';
import { destinazioneDopoLogin } from './postLogin';

interface GoogleSignInButtonProps {
  label?: string;
  className?: string;
}

// Niente fallback: deve coincidere col GOOGLE_CLIENT_ID del backend dello stesso ambiente.
// Un fallback hardcoded su un ambiente sbagliato → audience mismatch e login rotto in modo silenzioso.
const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID as string | undefined;
if (!GOOGLE_CLIENT_ID) {
  console.error('[GoogleSignInButton] VITE_GOOGLE_CLIENT_ID mancante: il login Google non funzionerà. Verifica frontend/.env (DEV) o frontend/.env.beta (BETA).');
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ label = 'Continua con Google', className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const { refreshUser } = useAuth();
  const { showError, showSuccess } = useToast();
  const API_BASE = import.meta.env.DEV ? 'http://localhost:3002/api' : '/api';

  useEffect(() => {
    const g = (window as any).google;
    if (g?.accounts?.id) {
      setReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const g = (window as any).google;
    if (!ready || !containerRef.current || !g?.accounts?.id) return;

    // Cancella eventuali prompt One Tap automatici
    try {
      g.accounts.id.cancel();
    } catch (e) {
      // Ignora errori se non c'è nessun prompt attivo
    }

    g.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      auto_select: false, // Disabilita auto-login silenzioso
      cancel_on_tap_outside: true,
      callback: async (response: any) => {
        try {
          const id_token = response?.credential;
          if (!id_token) return;
          const resp = await fetch(`${API_BASE}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id_token })
          });
          const text = await resp.text();
          if (!resp.ok) {
            // eslint-disable-next-line no-console
            console.error('Google auth backend error:', text);
            
            // Prova a parsare il messaggio di errore dal backend
            let errorMessage = 'Errore autenticazione Google';
            try {
              const errorData = JSON.parse(text);
              if (errorData?.error) {
                errorMessage = errorData.error;
              }
            } catch {
              // Se non riesce a parsare, usa il messaggio di default
            }
            
            showError('Errore Google Sign-In', errorMessage);
            return;
          }
          let data: any;
          try { data = JSON.parse(text); } catch { 
            showError('Errore Google Sign-In', 'Risposta server non valida');
            return; 
          }
          const token = data?.token ?? data?.access_token;
          const refresh = data?.refreshToken ?? data?.refresh_token;
          if (!token || !refresh) { 
            showError('Errore Google Sign-In', 'Dati di accesso non validi ricevuti dal server');
            return; 
          }
          localStorage.setItem('accessToken', token);
          localStorage.setItem('refreshToken', refresh);
          
          // Refresh user data per controllo status profilo
          let updatedUserData = null;
          try { 
            updatedUserData = await refreshUser(); 
          } catch (error) {
            console.warn('Warning: Impossibile aggiornare dati utente:', error);
          }
          
          // Check status profilo per redirect appropriato
          if ((updatedUserData as any)?.needs_profile_completion || (updatedUserData as any)?.status === 'pending_completion') {
            const user = updatedUserData as any;
            
            // Redirect intelligente basato sui permessi utente
            if (user.role === 'admin' || user.role === 'superadmin' || user.group_id === 'accettazione') {
              showSuccess('Accesso Google Admin', 'Reindirizzamento al form completo...');
              setTimeout(() => {
                window.location.replace(`/admin/profile-form?userId=${user.id}&email=${encodeURIComponent(user.email)}&from=google`);
              }, 1000);
            } else {
              showSuccess('Accesso Google Effettuato', 'Completa ora il tuo profilo per continuare');
              setTimeout(() => {
                window.location.replace('/complete-profile?from=google');
              }, 1000);
            }
          } else {
            showSuccess('Accesso Effettuato', 'Benvenuto! Reindirizzamento in corso...');
            setTimeout(() => {
              window.location.replace(destinazioneDopoLogin());
            }, 1000);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Errore Google auth:', e);
          showError('Errore Google Sign-In', 'Errore di comunicazione con il server');
        }
      },
      ux_mode: 'popup'
    });

    g.accounts.id.renderButton(containerRef.current, {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      shape: 'rectangular',
      text: 'continue_with',
      logo_alignment: 'left'
    });
  }, [ready, API_BASE, refreshUser]);

  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className={`${className} border-2 border-red-500 bg-red-50 text-red-900 rounded p-3 text-sm`}>
        <div className="font-bold mb-1">⚠️ Login Google non disponibile</div>
        <div>Configurazione mancante: <code className="bg-red-100 px-1 rounded">VITE_GOOGLE_CLIENT_ID</code> non impostata.</div>
        <div className="mt-1 text-xs opacity-80">Verifica <code>frontend/.env</code> (DEV) o <code>frontend/.env.beta</code> (BETA) e rideploya.</div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => { window.location.reload(); }}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
          >
            Ricarica pagina
          </button>
          <a
            href="/login"
            className="px-3 py-1 bg-white hover:bg-red-100 border border-red-400 text-red-700 rounded text-xs no-underline"
          >
            Usa email e password
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div ref={containerRef} />
      {!ready && (
        <button className="w-full flex items-center justify-center bg-white text-black py-2 rounded opacity-60 cursor-not-allowed">
          {label}
        </button>
      )}
    </div>
  );
};

export default GoogleSignInButton;
